import React from 'react';
import { PaperClipOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import type {
  ChatRole,
  ProposalStatus,
} from '../../../api/agentChat/interface';
import type { ClaimFile } from '../../../api/files/interface';
import type { Task } from '../../../api/tasks/interface';
import styles from '../Chat.module.scss';
import {
  type DisplayMessage,
  TERMINAL_PROPOSAL_STATUSES,
  formatDateLabel,
  getDateKey,
} from '../chatHelpers';
import MessageBubble from '../MessageBubble';
import ProposalCard from '../ProposalCard';

type MentionClickHandler = (type: 'file' | 'task', id: string) => void;

interface Props {
  displayMessages: DisplayMessage[];
  dossierId: string;
  threadId: string;
  loading: boolean;
  highlightedId: string | null;
  files: ClaimFile[];
  tasks: Task[];
  proposalSubmitting: Record<string, boolean>;
  handleApprove: (messageId: string, proposalId: string) => void;
  handleReject: (messageId: string, proposalId: string) => void;
  handleCitationClick: (fileId: string, page?: number) => void;
  handleMentionClick: MentionClickHandler;
  listRef: React.RefObject<HTMLDivElement>;
}

const MessageList: React.FC<Props> = ({
  displayMessages,
  dossierId,
  threadId,
  loading,
  highlightedId,
  files,
  tasks,
  proposalSubmitting,
  handleApprove,
  handleReject,
  handleCitationClick,
  handleMentionClick,
  listRef,
}) => {
  let lastDateKey: string | null = null;
  let lastUserText: string | undefined;
  const items: React.ReactNode[] = [];

  if (!loading) {
    for (const m of displayMessages) {
      const dateKey = getDateKey(m.created_at);
      if (dateKey && dateKey !== lastDateKey) {
        lastDateKey = dateKey;
        items.push(
          <div key={`date-${dateKey}`} className={styles.dateSeparator}>
            <div className={styles.dateLine} />
            <span className={styles.dateLabel}>{formatDateLabel(dateKey)}</span>
            <div className={styles.dateLine} />
          </div>
        );
      }

      if (m.role === 'SYSTEM') {
        items.push(
          <div key={m.message_id} className={styles.systemMessage}>
            <PaperClipOutlined className={styles.systemMessageIcon} />
            <span>{m.text} added to sources</span>
          </div>
        );
        continue;
      }

      const terminalProposals = (m.proposals ?? []).filter((p) =>
        TERMINAL_PROPOSAL_STATUSES.includes(p.status as ProposalStatus)
      );

      const query = lastUserText;
      if (m.role === 'USER') {
        lastUserText = m.text;
      }

      items.push(
        <MessageBubble
          key={m.message_id}
          role={m.role as ChatRole}
          text={m.text}
          createdAt={m.created_at}
          streaming={m.streaming}
          onCitationClick={handleCitationClick}
          messageId={m.message_id}
          dossierId={dossierId}
          threadId={threadId}
          query={query}
          highlighted={m.message_id === highlightedId}
          files={files}
          tasks={tasks}
          onMentionClick={handleMentionClick}
          artifactId={m.artifact_id}
          artifactName={m.artifact_name}
          actions={
            terminalProposals.length > 0 ? (
              <div className={styles.proposalsList}>
                {terminalProposals.map((p) => (
                  <ProposalCard
                    key={p.proposal_id}
                    proposal={p}
                    submitting={!!proposalSubmitting[p.proposal_id]}
                    onApprove={() => handleApprove(m.message_id, p.proposal_id)}
                    onReject={() => handleReject(m.message_id, p.proposal_id)}
                  />
                ))}
              </div>
            ) : undefined
          }
        />
      );
    }
  }

  return (
    <div className={styles.messageList} ref={listRef}>
      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : displayMessages.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <span className={styles.emptyTitle}>Owl Agent</span>
          </div>
          <p className={styles.emptyDesc}>
            Ask anything about this claim and receive answers without manually
            searching through notes, files, or summaries.
          </p>
        </div>
      ) : (
        <div className={styles.messages}>{items}</div>
      )}
    </div>
  );
};

export default MessageList;
